import { curryN } from 'ramda'

const invariant = (predicate, message) => {
  if (!predicate) {
    throw new Error(message)
  }
}

export default function ExtensionManager(name, data, parent, scope, store) {
  const next = (function () {
    let next = 0
    return () => {
      const id = next++ % 1000
      return `handler-${id}`
    }
  })()
  return {
    getDisplayName(acc) {
      if (!parent) {
        return acc
      } else {
        return parent.getDisplayName(`${name}.${acc}`)
      }
    },
    push(...configs) {
      invariant(configs.length > 0, 'Must pass at least one config')
      const id = scope._customJavaScriptAddonScope
        ? `addon-${scope._customJavaScriptAddonScope}`
        : next() // allows us to scope by custom JS addons if needed
      const matches = []

      for (let config of configs) {
        const { match, context, onEnter, head, ...rest } = config

        matches.push(match)

        store.loadExtensions(match, id, [{ match, context, onEnter, head, ...rest }])

        // If `onEnter` is provided and extension matches current path, invoke it right away safely.
        if (store.pathData[match]) {
          invokeSafelyWithWarning(() => {
            onEnter && onEnter.call(context, store.getElement(match), store.getData(match))
          })
        }
      }

      return () => {
        for (let match of matches) {
          store.removeExtensions(match, id)
        }
      }
    },
    updateData(d) {
      data && Object.assign(data, d)
    },

    onEnter: invoke(name, parent, data, store, 'onEnter', function (element, path, data) {
      store.activatePath(path, element, data)
    }),

    onLeave: invoke(name, parent, data, store, 'onLeave', function (element, path, data) {
      store.deactivatePath(path)
    }),
  }
}

const invoke = curryN(
  9,
  (name, parent, data, store, lifecycle, onHandled, element, path, accData) => {
    const mergedData = { ...accData, ...data }
    if (parent) {
      const newPath = [name].concat(path).join('.')
      parent[lifecycle](element, newPath, mergedData)
    } else {
      const pathHandlers = store.getHandlers(path)
      if (pathHandlers) {
        for (const extensions of pathHandlers.values()) {
          for (let ext of extensions) {
            if (typeof ext[lifecycle] === 'function') {
              invokeSafelyWithWarning(() => ext[lifecycle].call(ext.context, element, mergedData))
            }
          }
        }
      }
      onHandled(element, path, mergedData)
    }
  }
)

const invokeSafelyWithWarning = (fn) => {
  try {
    fn()
  } catch (e) {
    console.warn(e)
  }
}
